import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import Swal from 'sweetalert2';
import config from '../config';
const API = `${config.API_BASE_URL}/Flowchart`;
const STORAGE_KEY = "flowcraft_v5";
const MAX_UNDO = 60;
const DEF_W = 160, DEF_H = 60;
const MIN_W = 40, MIN_H = 24;

const getLoggedInUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user?.username) return { username: user.username, fullName: user.fullNames || user.username, role: "user" };
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.username) return { username: admin.username, fullName: admin.fullNames || admin.username, role: admin.role || "admin" };
  } catch (e) {}
  return { username: "guest", fullName: "Guest", role: "guest" };
};

const THEMES = {
  dark: { bg:"#0d1117",surface:"#161b22",panel:"#1c2333",border:"#21262d",accent:"#2f81f7",success:"#3fb950",danger:"#f85149",warning:"#d29922",text:"#e6edf3",muted:"#8b949e",subtle:"#30363d",hover:"#21262d",canvas:"#0d1117",gridColor:"#21262d",shadow:"0 8px 32px #000000aa" },
  light: { bg:"#f6f8fa",surface:"#ffffff",panel:"#f0f2f5",border:"#d0d7de",accent:"#0969da",success:"#1a7f37",danger:"#d1242f",warning:"#9a6700",text:"#1f2328",muted:"#656d76",subtle:"#d0d7de",hover:"#f3f4f6",canvas:"#f6f8fa",gridColor:"#c8d1da",shadow:"0 8px 32px #0000001a" }
};

const mkSwal = (T) => ({
  success:(m)=>Swal.fire({toast:true,position:"bottom-end",icon:"success",title:m,showConfirmButton:false,timer:2000,background:T.surface,color:T.text,iconColor:T.success}),
  error:  (m)=>Swal.fire({toast:true,position:"bottom-end",icon:"error",  title:m,showConfirmButton:false,timer:3000,background:T.surface,color:T.text,iconColor:T.danger}),
  warning:(m)=>Swal.fire({toast:true,position:"bottom-end",icon:"warning",title:m,showConfirmButton:false,timer:2000,background:T.surface,color:T.text,iconColor:T.warning}),
  info:   (m)=>Swal.fire({toast:true,position:"bottom-end",icon:"info",   title:m,showConfirmButton:false,timer:1800,background:T.surface,color:T.text,iconColor:T.accent}),
  confirm:(title,text)=>Swal.fire({title,text,icon:"warning",showCancelButton:true,confirmButtonText:"Yes",cancelButtonText:"Cancel",confirmButtonColor:T.danger,cancelButtonColor:T.subtle,background:T.surface,color:T.text,reverseButtons:true}),
  prompt: (title,ph,cur)=>Swal.fire({title,input:"text",inputValue:cur||"",inputPlaceholder:ph,showCancelButton:true,background:T.surface,color:T.text,confirmButtonColor:T.accent,inputValidator:v=>!v?.trim()?"Cannot be empty":null}),
});

// ─── SHAPES ───────────────────────────────────────
const SHAPE_CATS = {
  "Flowchart": [
    {type:"fc_start",   label:"Start/End",   icon:"⬭", w:140,h:50},
    {type:"fc_process", label:"Process",     icon:"▭", w:160,h:60},
    {type:"fc_decision",label:"Decision",    icon:"◆", w:140,h:70},
    {type:"fc_data",    label:"Data I/O",    icon:"▱", w:160,h:60},
    {type:"fc_document",label:"Document",    icon:"📄",w:160,h:70},
    {type:"fc_database",label:"Database",    icon:"🗄",w:120,h:80},
    {type:"fc_connector",label:"Connector",  icon:"⬤", w:60, h:60},
    {type:"fc_delay",   label:"Delay",       icon:"⬬", w:160,h:60},
    {type:"fc_manual",  label:"Manual Op",   icon:"⌨", w:160,h:60},
    {type:"fc_offpage", label:"Off-page",    icon:"⬠", w:140,h:60},
    {type:"fc_prep",    label:"Preparation", icon:"⬡", w:160,h:60},
    {type:"fc_merge",   label:"Merge",       icon:"▽", w:120,h:70},
    {type:"fc_extract", label:"Extract",     icon:"△", w:120,h:70},
    {type:"fc_sort",    label:"Sort",        icon:"⧖", w:120,h:70},
    {type:"fc_stored",  label:"Stored Data", icon:"🗄",w:120,h:80},
    {type:"fc_internal",label:"Internal",    icon:"▦", w:160,h:60},
    {type:"fc_term",    label:"Terminator",  icon:"⬬", w:160,h:50},
    {type:"fc_display", label:"Display",     icon:"🖥", w:160,h:70},
  ],
  "General": [
    {type:"g_rect",    label:"Rectangle",      icon:"▭", w:160,h:60},
    {type:"g_rounded", label:"Rounded Rect",   icon:"▢", w:160,h:60},
    {type:"g_ellipse", label:"Ellipse",        icon:"⬤", w:160,h:80},
    {type:"g_diamond", label:"Diamond",        icon:"◆", w:140,h:80},
    {type:"g_hex",     label:"Hexagon",        icon:"⬡", w:160,h:70},
    {type:"g_pentagon",label:"Pentagon",       icon:"⬠", w:140,h:80},
    {type:"g_triangle",label:"Triangle",       icon:"△", w:140,h:80},
    {type:"g_rtri",    label:"Right Triangle", icon:"◺", w:120,h:80},
    {type:"g_para",    label:"Parallelogram",  icon:"▱", w:160,h:60},
    {type:"g_trap",    label:"Trapezoid",      icon:"⏢", w:160,h:70},
    {type:"g_star4",   label:"Star 4pt",       icon:"✦", w:100,h:100},
    {type:"g_star5",   label:"Star 5pt",       icon:"★", w:100,h:100},
    {type:"g_star6",   label:"Star 6pt",       icon:"✡", w:100,h:100},
    {type:"g_cross",   label:"Cross/Plus",     icon:"✚", w:100,h:100},
    {type:"g_cloud",   label:"Cloud",          icon:"☁", w:160,h:90},
    {type:"g_callout", label:"Callout",        icon:"💬",w:160,h:80},
    {type:"g_note",    label:"Note",           icon:"🗒",w:140,h:80},
    {type:"g_cylinder",label:"Cylinder",       icon:"⬭", w:120,h:90},
    {type:"g_cube",    label:"Cube",           icon:"⬛",w:100,h:100},
    {type:"g_actor",   label:"Actor",          icon:"🧑",w:60, h:100},
  ],
  "Arrows": [
    // ── Fat arrows (filled block) ──
    {type:"a_right",   label:"Fat →",      icon:"➡", w:160,h:60},
    {type:"a_left",    label:"Fat ←",      icon:"⬅", w:160,h:60},
    {type:"a_up",      label:"Fat ↑",      icon:"⬆", w:60, h:120},
    {type:"a_down",    label:"Fat ↓",      icon:"⬇", w:60, h:120},
    {type:"a_bothH",   label:"Fat ↔",      icon:"↔", w:160,h:60},
    {type:"a_bothV",   label:"Fat ↕",      icon:"↕", w:60, h:120},
    {type:"a_chev",    label:"Chevron →",  icon:"❯", w:140,h:60},
    {type:"a_chev2",   label:"Chevron ↔",  icon:"»", w:160,h:60},
    {type:"a_notch",   label:"Notch →",    icon:"◁", w:160,h:60},
    {type:"a_step",    label:"Step Down",  icon:"⇒", w:160,h:60},
    // ── Thin arrows (line + arrowhead) ──
    {type:"at_right",  label:"Thin →",     icon:"→", w:160,h:40},
    {type:"at_left",   label:"Thin ←",     icon:"←", w:160,h:40},
    {type:"at_up",     label:"Thin ↑",     icon:"↑", w:40, h:120},
    {type:"at_down",   label:"Thin ↓",     icon:"↓", w:40, h:120},
    {type:"at_bothH",  label:"Thin ↔",     icon:"↔", w:160,h:40},
    {type:"at_bothV",  label:"Thin ↕",     icon:"↕", w:40, h:120},
    {type:"at_rightD", label:"Dbl Line →", icon:"⇒", w:160,h:40},
    {type:"at_curve",  label:"Curved ↷",   icon:"↷", w:120,h:80},
    {type:"at_zag",    label:"Zigzag →",   icon:"↝", w:160,h:50},
    {type:"at_open",   label:"Open →",     icon:"▷", w:140,h:50},
  ],
  "Basic": [
    {type:"b_card",   label:"Card",         icon:"🃏",w:120,h:80},
    {type:"b_frame",  label:"Frame/Group",  icon:"⬚", w:200,h:150},
    {type:"b_tag",    label:"Tag",          icon:"🏷",w:140,h:50},
    {type:"b_badge",  label:"Badge",        icon:"🔖",w:120,h:50},
    {type:"b_label",  label:"Label",        icon:"T", w:120,h:40},
    {type:"b_tape",   label:"Tape",         icon:"📼",w:160,h:60},
  ],
  "Entity Relation": [
    {type:"er_entity", label:"Entity",      icon:"▭", w:160,h:60},
    {type:"er_weak",   label:"Weak Entity", icon:"▭", w:160,h:60},
    {type:"er_attrib", label:"Attribute",   icon:"⬤", w:120,h:50},
    {type:"er_key",    label:"Key Attrib",  icon:"⊕", w:120,h:50},
    {type:"er_rel",    label:"Relation",    icon:"◆", w:140,h:70},
    {type:"er_isa",    label:"ISA",         icon:"△", w:120,h:70},
  ],
  "UML": [
    {type:"uml_class",  label:"Class",      icon:"▭", w:180,h:100},
    {type:"uml_iface",  label:"Interface",  icon:"⬤", w:160,h:70},
    {type:"uml_actor",  label:"Actor",      icon:"🧑",w:60, h:100},
    {type:"uml_usecase",label:"Use Case",   icon:"⬤", w:160,h:70},
    {type:"uml_pkg",    label:"Package",    icon:"📦",w:200,h:140},
    {type:"uml_note",   label:"Note",       icon:"🗒",w:160,h:80},
    {type:"uml_act",    label:"Activity",   icon:"⬭", w:160,h:50},
    {type:"uml_fork",   label:"Fork/Join",  icon:"—", w:160,h:10},
    {type:"uml_dec",    label:"Decision",   icon:"◆", w:60, h:60},
  ],
  "Network": [
    {type:"nw_server",  label:"Server",     icon:"🖥", w:80, h:90},
    {type:"nw_db",      label:"Database",   icon:"🗄",w:80, h:90},
    {type:"nw_cloud",   label:"Cloud",      icon:"☁", w:160,h:90},
    {type:"nw_router",  label:"Router",     icon:"⬡", w:80, h:80},
    {type:"nw_fw",      label:"Firewall",   icon:"🔥",w:120,h:60},
    {type:"nw_pc",      label:"PC/Client",  icon:"💻",w:80, h:80},
    {type:"nw_mobile",  label:"Mobile",     icon:"📱",w:60, h:90},
    {type:"nw_inet",    label:"Internet",   icon:"🌐",w:90, h:90},
  ],
  "Misc": [
    {type:"m_thought",  label:"Thought",    icon:"💭",w:160,h:80},
    {type:"m_wavy",     label:"Wavy",       icon:"〜",w:160,h:60},
    {type:"m_braceL",   label:"{ Brace L",  icon:"{", w:30, h:120},
    {type:"m_braceR",   label:"Brace R }",  icon:"}", w:30, h:120},
    {type:"m_brackL",   label:"[ Bracket",  icon:"[", w:20, h:100},
    {type:"m_brackR",   label:"Bracket ]",  icon:"]", w:20, h:100},
    {type:"m_sum",      label:"Sum Junct",  icon:"Σ", w:60, h:60},
    {type:"m_or",       label:"Or Junct",   icon:"○", w:60, h:60},
  ],
};

const ALL_SHAPES = Object.entries(SHAPE_CATS).flatMap(([cat,shapes])=>shapes.map(s=>({...s,cat})));

const COLOR_PRESETS = [
  "#8b949e","#2f81f7","#3fb950","#f85149","#d29922","#06b6d4",
  "#8b5cf6","#ec4899","#f97316","#10b981","#6366f1","#14b8a6",
  "#f43f5e","#84cc16","#ffffff","#000000",
];
const FONT_SIZES = [8,9,10,11,12,13,14,16,18,20,24,28,32,36,48];
const LINE_STYLES = ["solid","dashed","dotted"];
const CONN_STYLES = ["curved","straight","orthogonal","elbow"];

// ─── PATH GENERATOR ───────────────────────────────
function getPath(type, x, y, w, h) {
  switch(type) {
    case "fc_start": case "fc_term": {const r=h/2;return `M${x+r},${y} L${x+w-r},${y} Q${x+w},${y} ${x+w},${y+h/2} Q${x+w},${y+h} ${x+w-r},${y+h} L${x+r},${y+h} Q${x},${y+h} ${x},${y+h/2} Q${x},${y} ${x+r},${y} Z`;}
    case "fc_decision": case "g_diamond": case "er_rel": case "uml_dec":
      return `M${x+w/2},${y} L${x+w},${y+h/2} L${x+w/2},${y+h} L${x},${y+h/2} Z`;
    case "fc_data": case "g_para":
      return `M${x+h*0.3},${y} L${x+w},${y} L${x+w-h*0.3},${y+h} L${x},${y+h} Z`;
    case "fc_document":
      return `M${x},${y} L${x+w},${y} L${x+w},${y+h*0.78} Q${x+w*0.75},${y+h} ${x+w/2},${y+h*0.82} Q${x+w*0.25},${y+h*0.65} ${x},${y+h*0.82} Z`;
    case "fc_database": case "g_cylinder": case "er_entity": case "nw_db": case "uml_class":
      return `M${x},${y+12} Q${x},${y} ${x+w/2},${y} Q${x+w},${y} ${x+w},${y+12} L${x+w},${y+h-12} Q${x+w},${y+h} ${x+w/2},${y+h} Q${x},${y+h} ${x},${y+h-12} Z`;
    case "fc_connector": case "g_ellipse": case "uml_iface": case "uml_usecase": case "m_sum": case "m_or": case "nw_inet":
      return `M${x+w/2},${y} A${w/2},${h/2} 0 1,1 ${x+w/2-0.01},${y} Z`;
    case "fc_delay": {const r2=Math.min(w*0.3,h/2);return `M${x},${y} L${x+w-r2},${y} Q${x+w},${y} ${x+w},${y+h/2} Q${x+w},${y+h} ${x+w-r2},${y+h} L${x},${y+h} Z`;}
    case "fc_manual":  return `M${x},${y+h*0.18} L${x+w},${y} L${x+w},${y+h} L${x},${y+h} Z`;
    case "fc_offpage": case "a_step":
      return `M${x},${y} L${x+w},${y} L${x+w},${y+h*0.75} L${x+w/2},${y+h} L${x},${y+h*0.75} Z`;
    case "fc_prep": case "g_hex": case "nw_router":
      return `M${x+w*0.2},${y} L${x+w*0.8},${y} L${x+w},${y+h/2} L${x+w*0.8},${y+h} L${x+w*0.2},${y+h} L${x},${y+h/2} Z`;
    case "fc_merge":   return `M${x},${y} L${x+w},${y} L${x+w/2},${y+h} Z`;
    case "fc_extract": return `M${x+w/2},${y} L${x+w},${y+h} L${x},${y+h} Z`;
    case "fc_sort":    return `M${x+w/2},${y} L${x+w},${y+h/2} L${x+w/2},${y+h} L${x},${y+h/2} Z`;
    case "fc_stored":  return `M${x},${y+12} Q${x},${y} ${x+w/2},${y} Q${x+w},${y} ${x+w},${y+12} L${x+w},${y+h-12} Q${x+w},${y+h} ${x+w/2},${y+h} Q${x},${y+h} ${x},${y+h-12} Z`;
    case "fc_internal":return `M${x+16},${y} L${x+w},${y} L${x+w},${y+h} L${x+16},${y+h} L${x+16},${y}`;
    case "fc_display": return `M${x+h*0.3},${y} L${x+w-h*0.15},${y} Q${x+w},${y} ${x+w},${y+h/2} Q${x+w},${y+h} ${x+w-h*0.15},${y+h} L${x+h*0.3},${y+h} L${x},${y+h/2} Z`;
    case "fc_process": case "g_rect": case "er_weak": case "b_frame": case "uml_pkg": case "nw_fw": case "er_key":
      return `M${x},${y} L${x+w},${y} L${x+w},${y+h} L${x},${y+h} Z`;
    case "g_rounded": case "uml_act": case "b_badge": {const r=Math.min(10,w*0.1,h*0.4);return `M${x+r},${y} L${x+w-r},${y} Q${x+w},${y} ${x+w},${y+r} L${x+w},${y+h-r} Q${x+w},${y+h} ${x+w-r},${y+h} L${x+r},${y+h} Q${x},${y+h} ${x},${y+h-r} L${x},${y+r} Q${x},${y} ${x+r},${y} Z`;}
    case "g_pentagon": return `M${x+w/2},${y} L${x+w},${y+h*0.38} L${x+w*0.82},${y+h} L${x+w*0.18},${y+h} L${x},${y+h*0.38} Z`;
    case "g_triangle": return `M${x+w/2},${y} L${x+w},${y+h} L${x},${y+h} Z`;
    case "g_rtri":     return `M${x},${y} L${x+w},${y+h} L${x},${y+h} Z`;
    case "g_trap":     return `M${x+w*0.15},${y} L${x+w*0.85},${y} L${x+w},${y+h} L${x},${y+h} Z`;
    case "g_star4": {const cx=x+w/2,cy=y+h/2,ro=Math.min(w,h)*0.5,ri=ro*0.35;let p="M";for(let i=0;i<8;i++){const a=i*Math.PI/4-Math.PI/2,r=i%2===0?ro:ri;p+=(i>0?" L":"")+`${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`;}return p+" Z";}
    case "g_star5": {const cx=x+w/2,cy=y+h/2,ro=Math.min(w,h)*0.5,ri=ro*0.4;let p="M";for(let i=0;i<10;i++){const a=i*Math.PI*2/10-Math.PI/2,r=i%2===0?ro:ri;p+=(i>0?" L":"")+`${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`;}return p+" Z";}
    case "g_star6": {const cx=x+w/2,cy=y+h/2,ro=Math.min(w,h)*0.5,ri=ro*0.5;let p="M";for(let i=0;i<12;i++){const a=i*Math.PI*2/12-Math.PI/2,r=i%2===0?ro:ri;p+=(i>0?" L":"")+`${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`;}return p+" Z";}
    case "g_cross": {const t=Math.min(w,h)*0.28;return `M${x+w/2-t},${y} L${x+w/2+t},${y} L${x+w/2+t},${y+h/2-t} L${x+w},${y+h/2-t} L${x+w},${y+h/2+t} L${x+w/2+t},${y+h/2+t} L${x+w/2+t},${y+h} L${x+w/2-t},${y+h} L${x+w/2-t},${y+h/2+t} L${x},${y+h/2+t} L${x},${y+h/2-t} L${x+w/2-t},${y+h/2-t} Z`;}
    case "g_cloud":   return `M${x+w*0.22},${y+h*0.65} Q${x},${y+h*0.65} ${x},${y+h*0.42} Q${x},${y+h*0.1} ${x+w*0.28},${y+h*0.14} Q${x+w*0.32},${y} ${x+w*0.52},${y} Q${x+w*0.72},${y} ${x+w*0.74},${y+h*0.18} Q${x+w},${y+h*0.12} ${x+w},${y+h*0.4} Q${x+w},${y+h*0.65} ${x+w*0.78},${y+h*0.65} L${x+w*0.78},${y+h} L${x+w*0.22},${y+h} Z`;
    case "nw_cloud":  return `M${x+w*0.22},${y+h*0.65} Q${x},${y+h*0.65} ${x},${y+h*0.42} Q${x},${y+h*0.1} ${x+w*0.28},${y+h*0.14} Q${x+w*0.32},${y} ${x+w*0.52},${y} Q${x+w*0.72},${y} ${x+w*0.74},${y+h*0.18} Q${x+w},${y+h*0.12} ${x+w},${y+h*0.4} Q${x+w},${y+h*0.65} ${x+w*0.78},${y+h*0.65} L${x+w*0.78},${y+h} L${x+w*0.22},${y+h} Z`;
    case "g_callout": case "m_thought": return `M${x},${y} L${x+w},${y} L${x+w},${y+h*0.72} L${x+w*0.5},${y+h*0.72} L${x+w*0.35},${y+h} L${x+w*0.3},${y+h*0.72} L${x},${y+h*0.72} Z`;
    case "g_note": case "uml_note":   return `M${x},${y} L${x+w-18},${y} L${x+w},${y+18} L${x+w},${y+h} L${x},${y+h} Z`;
    case "g_cube": {const d=Math.min(w,h)*0.2;return `M${x+d},${y} L${x+w},${y} L${x+w},${y+h-d} L${x+w-d},${y+h} L${x},${y+h} L${x},${y+d} Z M${x+d},${y} L${x},${y+d} M${x+w},${y} L${x+w-d},${y+d} L${x},${y+d} M${x+w-d},${y+d} L${x+w-d},${y+h}`;}
    case "g_actor": case "uml_actor": return `M${x+w/2},${y+h*0.12} A${w*0.15},${h*0.12} 0 1,1 ${x+w/2-0.01},${y+h*0.12} Z M${x+w/2},${y+h*0.24} L${x+w/2},${y+h*0.62} M${x+w*0.15},${y+h*0.38} L${x+w*0.85},${y+h*0.38} M${x+w/2},${y+h*0.62} L${x+w*0.2},${y+h} M${x+w/2},${y+h*0.62} L${x+w*0.8},${y+h}`;
    // ── Fat block arrows ──
    case "a_right": case "a_chev": return `M${x},${y+h*0.25} L${x+w*0.65},${y+h*0.25} L${x+w*0.65},${y} L${x+w},${y+h/2} L${x+w*0.65},${y+h} L${x+w*0.65},${y+h*0.75} L${x},${y+h*0.75} Z`;
    case "a_left":  return `M${x+w},${y+h*0.25} L${x+w*0.35},${y+h*0.25} L${x+w*0.35},${y} L${x},${y+h/2} L${x+w*0.35},${y+h} L${x+w*0.35},${y+h*0.75} L${x+w},${y+h*0.75} Z`;
    case "a_up":    return `M${x+w*0.25},${y+h} L${x+w*0.25},${y+h*0.45} L${x},${y+h*0.45} L${x+w/2},${y} L${x+w},${y+h*0.45} L${x+w*0.75},${y+h*0.45} L${x+w*0.75},${y+h} Z`;
    case "a_down":  return `M${x+w*0.25},${y} L${x+w*0.25},${y+h*0.55} L${x},${y+h*0.55} L${x+w/2},${y+h} L${x+w},${y+h*0.55} L${x+w*0.75},${y+h*0.55} L${x+w*0.75},${y} Z`;
    case "a_bothH": return `M${x},${y+h/2} L${x+w*0.25},${y} L${x+w*0.25},${y+h*0.3} L${x+w*0.75},${y+h*0.3} L${x+w*0.75},${y} L${x+w},${y+h/2} L${x+w*0.75},${y+h} L${x+w*0.75},${y+h*0.7} L${x+w*0.25},${y+h*0.7} L${x+w*0.25},${y+h} Z`;
    case "a_bothV": return `M${x+w/2},${y} L${x+w},${y+h*0.25} L${x+w*0.7},${y+h*0.25} L${x+w*0.7},${y+h*0.75} L${x+w},${y+h*0.75} L${x+w/2},${y+h} L${x},${y+h*0.75} L${x+w*0.3},${y+h*0.75} L${x+w*0.3},${y+h*0.25} L${x},${y+h*0.25} Z`;
    case "a_chev2": return `M${x},${y} L${x+w*0.5},${y} L${x+w},${y+h/2} L${x+w*0.5},${y+h} L${x},${y+h} L${x+w*0.5},${y+h/2} Z`;
    case "a_notch": return `M${x},${y} L${x+w},${y} L${x+w-h*0.4},${y+h/2} L${x+w},${y+h} L${x},${y+h} L${x+h*0.4},${y+h/2} Z`;
    // ── Thin line arrows (open arrowhead) ──
    // → right: horizontal line + filled triangle head
    case "at_right": {
      const hy=y+h/2, hw=Math.min(h*0.9,w*0.25), hd=Math.min(h*0.5,hw*0.6);
      return `LINE:M${x},${hy} L${x+w-hw},${hy}|HEAD:M${x+w-hw},${hy-hd} L${x+w},${hy} L${x+w-hw},${hy+hd} Z`;
    }
    case "at_left": {
      const hy=y+h/2, hw=Math.min(h*0.9,w*0.25), hd=Math.min(h*0.5,hw*0.6);
      return `LINE:M${x+hw},${hy} L${x+w},${hy}|HEAD:M${x+hw},${hy-hd} L${x},${hy} L${x+hw},${hy+hd} Z`;
    }
    case "at_up": {
      const hx=x+w/2, hw=Math.min(w*0.9,h*0.25), hd=Math.min(w*0.5,hw*0.6);
      return `LINE:M${hx},${y+hw} L${hx},${y+h}|HEAD:M${hx-hd},${y+hw} L${hx},${y} L${hx+hd},${y+hw} Z`;
    }
    case "at_down": {
      const hx=x+w/2, hw=Math.min(w*0.9,h*0.25), hd=Math.min(w*0.5,hw*0.6);
      return `LINE:M${hx},${y} L${hx},${y+h-hw}|HEAD:M${hx-hd},${y+h-hw} L${hx},${y+h} L${hx+hd},${y+h-hw} Z`;
    }
    case "at_bothH": {
      const hy=y+h/2, hw=Math.min(h*0.9,w*0.2), hd=Math.min(h*0.5,hw*0.6);
      return `LINE:M${x+hw},${hy} L${x+w-hw},${hy}|HEAD:M${x+hw},${hy-hd} L${x},${hy} L${x+hw},${hy+hd} Z M${x+w-hw},${hy-hd} L${x+w},${hy} L${x+w-hw},${hy+hd} Z`;
    }
    case "at_bothV": {
      const hx=x+w/2, hw=Math.min(w*0.9,h*0.2), hd=Math.min(w*0.5,hw*0.6);
      return `LINE:M${hx},${y+hw} L${hx},${y+h-hw}|HEAD:M${hx-hd},${y+hw} L${hx},${y} L${hx+hd},${y+hw} Z M${hx-hd},${y+h-hw} L${hx},${y+h} L${hx+hd},${y+h-hw} Z`;
    }
    case "at_rightD": {
      const hy=y+h/2, gap=Math.min(h*0.2,5), hw=Math.min(h,w*0.22), hd=Math.min(h*0.55,hw*0.65);
      return `LINE:M${x},${hy-gap} L${x+w-hw},${hy-gap} M${x},${hy+gap} L${x+w-hw},${hy+gap}|HEAD:M${x+w-hw},${hy-hd} L${x+w},${hy} L${x+w-hw},${hy+hd} Z`;
    }
    case "at_curve": {
      const hw=Math.min(h*0.8,w*0.22), hd=Math.min(h*0.4,hw*0.6);
      return `LINE:M${x},${y+h} Q${x},${y} ${x+w-hw},${y}|HEAD:M${x+w-hw},${y-hd} L${x+w},${y} L${x+w-hw},${y+hd} Z`;
    }
    case "at_zag": {
      const hy=y+h/2, seg=w/5, hw=Math.min(h*0.8,w*0.2), hd=Math.min(h*0.45,hw*0.6);
      return `LINE:M${x},${hy} L${x+seg},${y+h*0.2} L${x+seg*2},${hy+h*0.25} L${x+seg*3},${y+h*0.15} L${x+w-hw},${hy}|HEAD:M${x+w-hw},${hy-hd} L${x+w},${hy} L${x+w-hw},${hy+hd} Z`;
    }
    case "at_open": {
      const hy=y+h/2, hw=Math.min(h*0.8,w*0.28), hd=Math.min(h*0.45,hw*0.6);
      return `LINE:M${x},${hy} L${x+w-hw},${hy}|HEAD:M${x+w-hw},${hy-hd} L${x+w},${hy} L${x+w-hw},${hy+hd}`;
    }
    case "b_card": case "b_frame": {const r=8;return `M${x+r},${y} L${x+w-r},${y} Q${x+w},${y} ${x+w},${y+r} L${x+w},${y+h-r} Q${x+w},${y+h} ${x+w-r},${y+h} L${x+r},${y+h} Q${x},${y+h} ${x},${y+h-r} L${x},${y+r} Q${x},${y} ${x+r},${y} Z`;}
    case "b_tag":   return `M${x},${y} L${x+w-16},${y} L${x+w},${y+h/2} L${x+w-16},${y+h} L${x},${y+h} Z`;
    case "b_label": return `M${x},${y+h/2} L${x+12},${y} L${x+w},${y} L${x+w},${y+h} L${x+12},${y+h} Z`;
    case "b_tape":  return `M${x},${y+h*0.2} Q${x+w*0.5},${y} ${x+w},${y+h*0.2} L${x+w},${y+h*0.8} Q${x+w*0.5},${y+h*0.6} ${x},${y+h*0.8} Z`;
    case "er_attrib":return `M${x+w/2},${y} A${w/2},${h/2} 0 1,1 ${x+w/2-0.01},${y} Z`;
    case "er_isa":   return `M${x+w/2},${y} L${x+w},${y+h} L${x},${y+h} Z`;
    case "uml_fork": return `M${x},${y+h/2} L${x+w},${y+h/2}`;
    case "nw_server":case "nw_pc":
      return `M${x},${y} L${x+w},${y} L${x+w},${y+h*0.75} L${x},${y+h*0.75} Z M${x+w/2},${y+h*0.75} L${x+w/2},${y+h} M${x+w*0.25},${y+h} L${x+w*0.75},${y+h}`;
    case "nw_mobile":
      return `M${x+w*0.15},${y} L${x+w*0.85},${y} Q${x+w},${y} ${x+w},${y+h*0.1} L${x+w},${y+h*0.9} Q${x+w},${y+h} ${x+w*0.85},${y+h} L${x+w*0.15},${y+h} Q${x},${y+h} ${x},${y+h*0.9} L${x},${y+h*0.1} Q${x},${y} ${x+w*0.15},${y} Z`;
    case "m_braceL":return `M${x+w},${y} Q${x},${y} ${x},${y+h/2} Q${x},${y+h} ${x+w},${y+h}`;
    case "m_braceR":return `M${x},${y} Q${x+w},${y} ${x+w},${y+h/2} Q${x+w},${y+h} ${x},${y+h}`;
    case "m_brackL":return `M${x+w},${y} L${x},${y} L${x},${y+h} L${x+w},${y+h}`;
    case "m_brackR":return `M${x},${y} L${x+w},${y} L${x+w},${y+h} L${x},${y+h}`;
    case "m_wavy":  return `M${x},${y+h/2} Q${x+w*0.25},${y} ${x+w*0.5},${y+h/2} Q${x+w*0.75},${y+h} ${x+w},${y+h/2}`;
    default:
      return `M${x+6},${y} L${x+w-6},${y} Q${x+w},${y} ${x+w},${y+6} L${x+w},${y+h-6} Q${x+w},${y+h} ${x+w-6},${y+h} L${x+6},${y+h} Q${x},${y+h} ${x},${y+h-6} L${x},${y+6} Q${x},${y} ${x+6},${y} Z`;
  }
}

// ─── RESIZE HANDLES ───────────────────────────────
const HANDLES = [
  {id:"nw",cx:0,cy:0},{id:"n",cx:0.5,cy:0},{id:"ne",cx:1,cy:0},
  {id:"w",cx:0,cy:0.5},                     {id:"e",cx:1,cy:0.5},
  {id:"sw",cx:0,cy:1},{id:"s",cx:0.5,cy:1},{id:"se",cx:1,cy:1},
];
const CURSORS = {nw:"nw-resize",n:"n-resize",ne:"ne-resize",w:"w-resize",e:"e-resize",sw:"sw-resize",s:"s-resize",se:"se-resize"};

// ─── ARROW ────────────────────────────────────────
function Arrow({edge,nodes,selected,T,onSelect,onDblClick}) {
  const fn=nodes.find(n=>n.id===edge.from),tn=nodes.find(n=>n.id===edge.to);
  if(!fn||!tn)return null;
  const fc={x:fn.x+fn.w/2,y:fn.y+fn.h/2},tc={x:tn.x+tn.w/2,y:tn.y+tn.h/2};
  let path;
  const st=edge.connStyle||"curved";
  if(st==="straight"){path=`M${fc.x},${fc.y} L${tc.x},${tc.y}`;}
  else if(st==="orthogonal"||st==="elbow"){const mx=(fc.x+tc.x)/2;path=`M${fc.x},${fc.y} L${mx},${fc.y} L${mx},${tc.y} L${tc.x},${tc.y}`;}
  else{const my=(fc.y+tc.y)/2;path=`M${fc.x},${fc.y} C${fc.x},${my} ${tc.x},${my} ${tc.x},${tc.y}`;}
  const ang=Math.atan2(tc.y-fc.y,tc.x-fc.x)*180/Math.PI+90;
  const col=selected?T.accent:(edge.color||T.muted);
  const mx=(fc.x+tc.x)/2,my2=(fc.y+tc.y)/2;
  const dash=edge.lineStyle==="dashed"?"8 4":edge.lineStyle==="dotted"?"2 4":"none";
  return (
    <g>
      <path d={path} stroke="transparent" strokeWidth={18} fill="none" style={{cursor:"pointer"}}
        onClick={e=>{e.stopPropagation();onSelect(edge.id);}}
        onDoubleClick={e=>{e.stopPropagation();onDblClick(edge.id,edge.label);}}/>
      <path d={path} stroke={col} strokeWidth={selected?2.5:1.8} fill="none" strokeDasharray={dash} style={{pointerEvents:"none"}}/>
      <polygon points="0,-10 7,4 -7,4" fill={col} transform={`translate(${tc.x},${tc.y}) rotate(${ang})`} style={{pointerEvents:"none"}}/>
      {edge.label&&(<g style={{pointerEvents:"none"}}>
        <rect x={mx-edge.label.length*3.5-6} y={my2-10} width={edge.label.length*7+12} height={18} rx={4} fill={T.surface} stroke={T.border} strokeWidth={1}/>
        <text x={mx} y={my2+2} textAnchor="middle" fill={T.muted} fontSize={10} fontFamily="monospace">{edge.label}</text>
      </g>)}
    </g>
  );
}

// ─── FLOW NODE ────────────────────────────────────
const THIN_ARROW_TYPES = new Set(["at_right","at_left","at_up","at_down","at_bothH","at_bothV","at_rightD","at_curve","at_zag","at_open"]);
const WIRE_TYPES = new Set(["m_braceL","m_braceR","m_brackL","m_brackR","m_wavy","uml_fork","g_actor","uml_actor"]);

function FlowNode({node,selected,T,onPointerDown,onDblClick,onResizeStart}) {
  const {x,y,w,h,type,label="",color="#8b949e",fillColor,textColor,fontSize=12,bold,italic}=node;
  const pathD=getPath(type,x,y,w,h);
  const lines=label.split(/\\n|\n/);
  const isWire=WIRE_TYPES.has(type);
  const isThinArrow=THIN_ARROW_TYPES.has(type);
  const fill=fillColor&&fillColor!=="transparent"?fillColor:(selected?color+"22":color+"12");
  const strokeW=isThinArrow?(selected?3:2.2):(selected?2.5:1.5);
  const fillVal=(isWire||isThinArrow)?"none":fill;
  // thin arrows: pathD = "LINE:...|HEAD:..."
  const thinParts = isThinArrow && pathD.startsWith("LINE:") ? pathD.split("|") : null;
  const thinLine = thinParts ? thinParts[0].replace("LINE:","") : null;
  const thinHead = thinParts ? thinParts[1].replace("HEAD:","") : null;
  const isOpenHead = type==="at_open";
  return (
    <g>
      {isThinArrow&&(
        <>
          {/* invisible hit area */}
          <path d={thinLine||pathD} fill="none" stroke="transparent" strokeWidth={24}
            style={{cursor:"move"}}
            onPointerDown={e=>onPointerDown(e,node)}
            onDoubleClick={e=>{e.stopPropagation();onDblClick(node.id);}}/>
          {/* shaft line */}
          <path d={thinLine||pathD} fill="none" stroke={color} strokeWidth={strokeW}
            strokeLinecap="round"
            style={{filter:selected?`drop-shadow(0 0 6px ${color}88)`:"none",pointerEvents:"none"}}/>
          {/* arrowhead - filled solid */}
          {thinHead&&<path d={thinHead} fill={isOpenHead?"none":color} stroke={color} strokeWidth={isOpenHead?strokeW:1}
            strokeLinejoin="round" style={{pointerEvents:"none"}}/>}
        </>
      )}
      {!isThinArrow&&<path d={pathD} fill={fillVal} stroke={color} strokeWidth={strokeW}
        strokeLinecap="round" strokeLinejoin="round"
        style={{filter:selected?`drop-shadow(0 0 8px ${color}88)`:"drop-shadow(0 2px 4px #00000044)",cursor:"move"}}
        onPointerDown={e=>onPointerDown(e,node)}
        onDoubleClick={e=>{e.stopPropagation();onDblClick(node.id);}}/>}
      {!isWire&&!isThinArrow&&lines.map((line,i)=>(
        <text key={i} x={x+w/2} y={y+h/2+(i-(lines.length-1)/2)*((fontSize||12)+4)}
          textAnchor="middle" dominantBaseline="middle"
          fill={textColor||color} fontSize={fontSize}
          fontWeight={bold?"700":"400"} fontStyle={italic?"italic":"normal"}
          fontFamily="'Segoe UI',system-ui,sans-serif"
          style={{pointerEvents:"none",userSelect:"none"}}>{line}</text>
      ))}
      {selected&&HANDLES.map(hh=>(
        <rect key={hh.id} x={x+w*hh.cx-5} y={y+h*hh.cy-5} width={10} height={10}
          rx={2} fill={T.accent} stroke={T.surface} strokeWidth={1.5}
          style={{cursor:CURSORS[hh.id],pointerEvents:"all"}}
          onPointerDown={e=>{e.stopPropagation();onResizeStart(e,node,hh.id);}}/>
      ))}
    </g>
  );
}

// ─── COLOR PICKER ─────────────────────────────────
function ColorPicker({current,onChange,T}) {
  const [custom,setCustom]=useState(current||"#8b949e");
  useEffect(()=>setCustom(current||"#8b949e"),[current]);
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:3,marginBottom:5}}>
        {COLOR_PRESETS.map(c=>(<div key={c} onClick={()=>onChange(c)} style={{aspectRatio:"1",borderRadius:3,background:c,cursor:"pointer",border:current===c?`2px solid ${T.text}`:`1px solid ${T.border}`,transition:"all 0.1s"}}/>))}
      </div>
      <div style={{display:"flex",gap:4}}>
        <input type="color" value={custom} onChange={e=>setCustom(e.target.value)} style={{width:26,height:22,border:`1px solid ${T.border}`,background:T.panel,cursor:"pointer",padding:0,borderRadius:3}}/>
        <input type="text" value={custom} onChange={e=>setCustom(e.target.value)} onKeyDown={e=>e.key==="Enter"&&onChange(custom)}
          style={{flex:1,background:T.panel,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,padding:"2px 5px",fontSize:10,fontFamily:"monospace",outline:"none"}}/>
        <button onClick={()=>onChange(custom)} style={{padding:"2px 6px",borderRadius:3,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:10,fontWeight:700}}>✔</button>
      </div>
    </div>
  );
}

// ─── SHAPE LIBRARY ────────────────────────────────
function ShapeLibrary({onAdd,onDragStart,T}) {
  const [search,setSearch]=useState("");
  const [expanded,setExpanded]=useState({Flowchart:true,General:true,Arrows:false,Basic:false,"Entity Relation":false,UML:false,Network:false,Misc:false});
  const toggle=c=>setExpanded(p=>({...p,[c]:!p[c]}));
  const filtered=search.trim()?ALL_SHAPES.filter(s=>s.label.toLowerCase().includes(search.toLowerCase())||s.type.includes(search)):null;
  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{padding:"7px 6px 4px",flexShrink:0}}>
        <div style={{position:"relative"}}>
          <span style={{position:"absolute",left:7,top:"50%",transform:"translateY(-50%)",fontSize:11,color:T.muted}}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search shapes..."
            style={{width:"100%",boxSizing:"border-box",background:T.panel,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,padding:"5px 8px 5px 24px",fontSize:11,outline:"none"}}/>
          {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:5,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:12}}>✕</button>}
        </div>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"0 4px 10px"}}>
        {filtered?(
          <>
            <div style={{fontSize:9,color:T.muted,padding:"4px 3px",letterSpacing:2,textTransform:"uppercase"}}>{filtered.length} result{filtered.length!==1?"s":""}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3}}>{filtered.map(s=><SI key={s.type} s={s} onAdd={onAdd} onDragStart={onDragStart} T={T}/>)}</div>
          </>
        ):Object.entries(SHAPE_CATS).map(([cat,shapes])=>(
          <div key={cat}>
            <button onClick={()=>toggle(cat)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"5px 3px",background:"none",border:"none",cursor:"pointer",color:T.muted,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5}}>
              <span>{expanded[cat]?"▾":"▸"} {cat}</span>
              <span style={{fontSize:9,opacity:0.5}}>{shapes.length}</span>
            </button>
            {expanded[cat]&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3,marginBottom:5}}>{shapes.map(s=><SI key={s.type} s={s} onAdd={onAdd} onDragStart={onDragStart} T={T}/>)}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
function SI({s,onAdd,onDragStart,T}) {
  const [hov,setHov]=useState(false);
  return (
    <div draggable onDragStart={e=>onDragStart(e,s)} onClick={()=>onAdd(s.type,s.label,s.w,s.h)} title={s.label}
      style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"6px 2px",borderRadius:4,border:`1px solid ${hov?T.accent:T.border}`,background:hov?T.hover:T.panel,cursor:"grab",userSelect:"none",gap:2,transition:"all 0.1s"}}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}>
      <span style={{fontSize:13,lineHeight:1}}>{s.icon}</span>
      <span style={{fontSize:8,color:T.muted,textAlign:"center",lineHeight:1.1,wordBreak:"break-all"}}>{s.label}</span>
    </div>
  );
}

// ─── PAGE TABS ────────────────────────────────────
function PageTabs({pages,activeId,onSwitch,onAdd,onRename,onDelete,T}) {
  return (
    <div style={{display:"flex",alignItems:"flex-end",background:T.surface,borderTop:`1px solid ${T.border}`,flexShrink:0,height:30,overflowX:"auto",paddingLeft:5}}>
      {pages.map(p=>(
        <div key={p.id}
          style={{display:"flex",alignItems:"center",gap:3,padding:"2px 9px 2px 10px",background:p.id===activeId?T.bg:T.panel,border:`1px solid ${T.border}`,borderBottom:p.id===activeId?`1px solid ${T.bg}`:"none",borderRadius:"4px 4px 0 0",cursor:"pointer",fontSize:11,color:p.id===activeId?T.text:T.muted,fontWeight:p.id===activeId?600:400,userSelect:"none",position:"relative",bottom:-1,whiteSpace:"nowrap"}}
          onClick={()=>onSwitch(p.id)} onDoubleClick={()=>onRename(p.id,p.name)}>
          {p.name}
          {pages.length>1&&<span onClick={e=>{e.stopPropagation();onDelete(p.id);}} style={{fontSize:9,color:T.muted,cursor:"pointer",padding:"0 1px"}} onMouseEnter={e=>e.currentTarget.style.color=T.danger} onMouseLeave={e=>e.currentTarget.style.color=T.muted}>✕</span>}
        </div>
      ))}
      <button onClick={onAdd} style={{padding:"2px 10px",borderRadius:"4px 4px 0 0",border:`1px solid ${T.border}`,borderBottom:"none",background:T.panel,color:T.muted,cursor:"pointer",fontSize:14,position:"relative",bottom:-1}}>＋</button>
    </div>
  );
}

// ─── MODAL WRAP ───────────────────────────────────
function Modal({onClose,title,children,wide,T}) {
  return (
    <div style={{position:"fixed",inset:0,background:"#00000099",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500}} onClick={onClose}>
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:20,width:wide?"650px":"390px",maxWidth:"95vw",maxHeight:"88vh",overflow:"auto",boxShadow:T.shadow}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"center",marginBottom:13}}>
          <span style={{fontSize:13,fontWeight:700,color:T.accent,flex:1}}>{title}</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:17}}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
const mI=(T)=>({width:"100%",background:T.panel,border:`1px solid ${T.border}`,borderRadius:4,color:T.text,padding:"5px 8px",fontSize:12,outline:"none",boxSizing:"border-box"});
const mB=(T,col,outline)=>({padding:"4px 11px",borderRadius:4,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${col||T.border}`,background:outline?"transparent":col||T.panel,color:outline?col:"#fff",transition:"all 0.1s"});

// ─── SAVE MODAL ───────────────────────────────────
function SaveModal({pages,folders,currentId,currentTitle,onClose,onSaved,T}) {
  const IS=mI(T);const [title,setTitle]=useState(currentTitle||"Untitled");const [desc,setDesc]=useState("");const [fid,setFid]=useState("");const [busy,setBusy]=useState(false);const swal=mkSwal(T);
  const doSave=async()=>{if(!title.trim())return swal.warning("Enter a title!");setBusy(true);
    const allNodes=pages.flatMap(p=>p.nodes.map(n=>({nodeKey:n.id,type:n.type,label:n.label,color:n.color||"#8b949e",x:n.x,y:n.y})));
    const allEdges=pages.flatMap(p=>p.edges.map(e=>({edgeKey:e.id,fromNodeKey:e.from,toNodeKey:e.to,label:e.label||""})));
    const body={title:title.trim(),description:desc,folderId:fid?Number(fid):null,username:getLoggedInUser().username,nodes:allNodes,edges:allEdges};
    try{const res=await fetch(currentId?`${API}/${currentId}`:API,{method:currentId?"PUT":"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});if(!res.ok)throw new Error(await res.text());const data=await res.json();swal.success("Saved!");onSaved(data.id,title.trim());onClose();}
    catch(e){swal.error("Save failed: "+e.message);}finally{setBusy(false);}
  };
  return (
    <Modal onClose={onClose} title="💾 Save Flowchart" T={T}>
      <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:4}}>Title *</div>
      <input style={IS} value={title} onChange={e=>setTitle(e.target.value)} autoFocus/>
      <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:4,marginTop:8}}>Description</div>
      <textarea style={{...IS,minHeight:55,resize:"vertical"}} value={desc} onChange={e=>setDesc(e.target.value)}/>
      <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:4,marginTop:8}}>Folder</div>
      <select style={IS} value={fid} onChange={e=>setFid(e.target.value)}><option value="">📂 No folder</option>{folders.map(f=><option key={f.id} value={f.id}>📁 {f.name}</option>)}</select>
      <div style={{display:"flex",gap:7,marginTop:14}}>
        <button style={{...mB(T,T.accent),flex:1}} onClick={doSave} disabled={busy}>{busy?"Saving...":currentId?"🔄 Update":"💾 Save"}</button>
        <button style={{...mB(T),flex:1}} onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

// ─── LOAD MODAL ───────────────────────────────────
function LoadModal({onClose,onLoad,T}) {
  const [list,setList]=useState([]);const [folders,setFolders]=useState([]);const [trash,setTrash]=useState([]);const [loading,setLoading]=useState(true);const [tab,setTab]=useState("all");const [selFol,setSelFol]=useState(null);const swal=mkSwal(T);
  const LS={row:{display:"flex",alignItems:"center",gap:7,padding:"7px 9px",borderRadius:6,border:`1px solid ${T.border}`,background:T.panel,marginBottom:5}};
  const load=async()=>{setLoading(true);try{const[a,b,c]=await Promise.all([fetch(API).then(r=>r.json()),fetch(`${API}/folders`).then(r=>r.json()),fetch(`${API}/deleted`).then(r=>r.json())]);setList(a);setFolders(b);setTrash(c);}catch(e){swal.error(e.message);}setLoading(false);};
  useEffect(()=>{load();},[]);
  const loadFC=async(id,t)=>{const r=await swal.confirm(`Load "${t}"?`,"Unsaved changes lost.");if(!r.isConfirmed)return;try{const res=await fetch(`${API}/${id}`);const data=await res.json();onLoad(data);onClose();swal.success(`"${t}" loaded!`);}catch(e){swal.error(e.message);}};
  const deleteFC=async(id,t)=>{const r=await swal.confirm(`Delete "${t}"?`,"Moves to trash.");if(!r.isConfirmed)return;try{await fetch(`${API}/${id}`,{method:"DELETE"});swal.success("Moved to trash.");load();}catch(e){swal.error(e.message);}};
  const restoreFC=async(id,t)=>{try{await fetch(`${API}/${id}/restore`,{method:"PUT"});swal.success(`"${t}" restored!`);load();}catch(e){swal.error(e.message);}};
  const createFolder=async()=>{const r=await swal.prompt("New Folder","Name...","");if(!r.isConfirmed||!r.value?.trim())return;try{await fetch(`${API}/folders`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({name:r.value.trim(),username:getLoggedInUser().username})});load();swal.success("Created!");}catch(e){swal.error(e.message);}};
  const Row=({f})=>(<div style={LS.row}><div style={{flex:1,minWidth:0}}><div style={{fontSize:12,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.title}</div><div style={{fontSize:10,color:T.muted}}>{f.nodeCount||0}n · {f.edgeCount||0}e · {new Date(f.modifiedAt).toLocaleDateString()}</div></div><button onClick={()=>loadFC(f.id,f.title)} style={{...mB(T,T.accent),fontSize:10,padding:"2px 9px"}}>Load</button><button onClick={()=>deleteFC(f.id,f.title)} style={{...mB(T,T.danger,true),fontSize:10,padding:"2px 7px"}}>🗑</button></div>);
  return (
    <Modal onClose={onClose} title="📂 Library" wide T={T}>
      <div style={{display:"flex",gap:4,marginBottom:10,borderBottom:`1px solid ${T.border}`,paddingBottom:7}}>
        {[["all","All"],["folders","📁 Folders"],["trash","🗑 Trash"]].map(([k,l])=>(<button key={k} onClick={()=>setTab(k)} style={{...mB(T,tab===k?T.accent:undefined,tab!==k),padding:"3px 11px",fontSize:11}}>{l}</button>))}
        <div style={{flex:1}}/><button onClick={load} style={{...mB(T),padding:"3px 7px",fontSize:11}}>⟳</button>
      </div>
      {loading?<div style={{textAlign:"center",padding:36,color:T.muted}}>Loading...</div>
      :tab==="trash"?(<div>{trash.length===0?<div style={{textAlign:"center",padding:30,color:T.muted}}>Trash empty</div>:trash.map(f=>(<div key={f.id} style={LS.row}><div style={{flex:1}}><div style={{fontSize:12,color:T.text}}>{f.title}</div><div style={{fontSize:10,color:T.muted}}>{new Date(f.modifiedAt).toLocaleDateString()}</div></div><button onClick={()=>restoreFC(f.id,f.title)} style={{...mB(T,T.success),fontSize:10}}>↩ Restore</button></div>))}</div>)
      :tab==="folders"?(<div><div style={{display:"flex",justifyContent:"flex-end",marginBottom:7}}><button style={{...mB(T,T.accent),fontSize:11}} onClick={createFolder}>📁 + New Folder</button></div>{folders.length===0?<div style={{textAlign:"center",padding:30,color:T.muted}}>No folders</div>:folders.map(fo=>(<div key={fo.id} style={{...LS.row,flexDirection:"column",alignItems:"flex-start"}}><div style={{display:"flex",width:"100%",alignItems:"center",gap:6}}><span style={{fontSize:13}}>📁</span><div style={{flex:1}}><div style={{fontSize:12,fontWeight:600,color:T.text}}>{fo.name}</div><div style={{fontSize:10,color:T.muted}}>{fo.flowchartCount} item(s)</div></div><button onClick={()=>setSelFol(selFol===fo.id?null:fo.id)} style={{...mB(T,T.accent,true),fontSize:10,padding:"2px 7px"}}>{selFol===fo.id?"▲":"▼"}</button></div>{selFol===fo.id&&<div style={{width:"100%",paddingLeft:16,borderLeft:`2px solid ${T.border}`,marginTop:4}}>{list.filter(f=>f.folderId===fo.id).length===0?<div style={{fontSize:10,color:T.muted,padding:"4px 0"}}>Empty</div>:list.filter(f=>f.folderId===fo.id).map(f=><Row key={f.id} f={f}/>)}</div>}</div>))}</div>)
      :(<div>{list.length===0?<div style={{textAlign:"center",padding:30,color:T.muted}}>No saved flowcharts</div>:list.map(f=><Row key={f.id} f={f}/>)}</div>)}
    </Modal>
  );
}

// ─── TOOLBAR BTN ──────────────────────────────────
function TB({onClick,title,active,disabled,danger,children,T}) {
  const [hov,setHov]=useState(false);
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      style={{display:"flex",alignItems:"center",gap:3,padding:"3px 7px",borderRadius:4,border:"none",cursor:disabled?"default":"pointer",fontSize:11,fontWeight:500,background:active?T.accent:hov?T.hover:"transparent",color:disabled?T.subtle:active?"#fff":danger?T.danger:T.text,opacity:disabled?0.4:1,whiteSpace:"nowrap",transition:"all 0.1s"}}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}>{children}</button>
  );
}

let idC=1000;const uid=()=>`${Date.now()}-${++idC}`;

// ════════════════════════════════════════════════════
// MAIN APP
// ════════════════════════════════════════════════════
export default function App() {
  const [isDark,setIsDark]=useState(()=>{try{return localStorage.getItem("fc_theme")!=="light";}catch{return true;}});
  const T=THEMES[isDark?"dark":"light"];
  const swal=useMemo(()=>mkSwal(T),[isDark]);

  const initData=useMemo(()=>{
    try{const s=localStorage.getItem(STORAGE_KEY);if(s){const d=JSON.parse(s);if(d.pages?.length>0)return d;}}catch{}
    const pid=uid();return{pages:[{id:pid,name:"Page-1",nodes:[],edges:[]}],activeId:pid};
  },[]);

  const [pages,setPages]=useState(initData.pages);
  const [activeId,setActiveId]=useState(initData.pages[0].id);
  const [selNode,setSelNode]=useState(null);
  const [selEdge,setSelEdge]=useState(null);
  const [history,setHistory]=useState([{pages:initData.pages}]);
  const [histIdx,setHistIdx]=useState(0);
  const [drag,setDrag]=useState(null);
  const [resize,setResize]=useState(null);
  const [connecting,setConn]=useState(null);
  const [connectMode,setConnMode]=useState(false);
  const [mouse,setMouse]=useState({x:0,y:0});
  const [zoom,setZoom]=useState(1);
  const [pan,setPan]=useState({x:60,y:40});
  const [panDrag,setPanDrag]=useState(null);
  const [editModal,setEditModal]=useState(null);
  const [editVal,setEditVal]=useState("");
  const [showSave,setShowSave]=useState(false);
  const [showLoad,setShowLoad]=useState(false);
  const [currentId,setCurrentId]=useState(null);
  const [currentTitle,setCurrentTitle]=useState("Untitled Flowchart");
  const [folders,setFolders]=useState([]);
  const [localSaved,setLocalSaved]=useState(false);
  const [isFS,setIsFS]=useState(false);
  const svgRef=useRef(null);
  const rootRef=useRef(null);
  const dragShapeRef=useRef(null);

  const activePage=useMemo(()=>pages.find(p=>p.id===activeId)||pages[0],[pages,activeId]);
  const nodes=activePage?.nodes||[];
  const edges=activePage?.edges||[];
  const setNodes=useCallback(fn=>setPages(pp=>pp.map(p=>p.id===activeId?{...p,nodes:typeof fn==="function"?fn(p.nodes):fn}:p)),[activeId]);
  const setEdges=useCallback(fn=>setPages(pp=>pp.map(p=>p.id===activeId?{...p,edges:typeof fn==="function"?fn(p.edges):fn}:p)),[activeId]);

  const pushH=useCallback((np)=>{setHistory(h=>[...h.slice(0,histIdx+1),{pages:np}].slice(-MAX_UNDO));setHistIdx(i=>Math.min(i+1,MAX_UNDO-1));},[histIdx]);
  const undo=useCallback(()=>{if(histIdx<=0){swal.warning("Nothing to undo.");return;}const p=history[histIdx-1];setPages(p.pages);setHistIdx(i=>i-1);setSelNode(null);setSelEdge(null);swal.info("Undone!");},[history,histIdx]);
  const redo=useCallback(()=>{if(histIdx>=history.length-1){swal.warning("Nothing to redo.");return;}const p=history[histIdx+1];setPages(p.pages);setHistIdx(i=>i+1);setSelNode(null);setSelEdge(null);swal.info("Redone!");},[history,histIdx]);

  useEffect(()=>{try{localStorage.setItem(STORAGE_KEY,JSON.stringify({pages,activeId}));setLocalSaved(true);const t=setTimeout(()=>setLocalSaved(false),1000);return()=>clearTimeout(t);}catch{}},[pages,activeId]);
  useEffect(()=>{fetch(`${API}/folders`).then(r=>r.json()).then(setFolders).catch(()=>{});},[]);

  const toggleTheme=()=>{setIsDark(d=>{try{localStorage.setItem("fc_theme",d?"light":"dark");}catch{}return!d;});};
  const toggleFS=()=>{if(!document.fullscreenElement)rootRef.current?.requestFullscreen().then(()=>setIsFS(true)).catch(()=>{});else document.exitFullscreen().then(()=>setIsFS(false)).catch(()=>{});};
  useEffect(()=>{const fn=()=>setIsFS(!!document.fullscreenElement);document.addEventListener("fullscreenchange",fn);return()=>document.removeEventListener("fullscreenchange",fn);},[]);

  const addPage=()=>{const id=uid(),np=[...pages,{id,name:`Page-${pages.length+1}`,nodes:[],edges:[]}];setPages(np);setActiveId(id);pushH(np);};
  const deletePage=id=>{if(pages.length<=1){swal.warning("Can't delete last page!");return;}const np=pages.filter(p=>p.id!==id);setPages(np);if(activeId===id)setActiveId(np[0].id);pushH(np);swal.success("Page deleted.");};
  const renamePage=async(id,cur)=>{const r=await swal.prompt("Rename Page","Name...",cur);if(!r.isConfirmed||!r.value?.trim())return;const np=pages.map(p=>p.id===id?{...p,name:r.value.trim()}:p);setPages(np);pushH(np);};

  const addNode=useCallback((type,label,nw=DEF_W,nh=DEF_H)=>{
    const el=svgRef.current,cx=el?el.clientWidth/2:400,cy=el?el.clientHeight/2:300;
    const id=uid();
    const nn={id,type,x:(cx-pan.x)/zoom-nw/2+(Math.random()-.5)*80,y:(cy-pan.y)/zoom-nh/2+(Math.random()-.5)*60,w:nw,h:nh,label:label||type,color:"#8b949e"};
    const np=pages.map(p=>p.id===activeId?{...p,nodes:[...p.nodes,nn]}:p);
    setPages(np);setSelNode(id);setSelEdge(null);pushH(np);
  },[pages,activeId,pan,zoom,pushH]);

  const onShapeDragStart=(e,s)=>{dragShapeRef.current=s;e.dataTransfer.effectAllowed="copy";};
  const onCanvasDragOver=e=>{e.preventDefault();e.dataTransfer.dropEffect="copy";};
  const onCanvasDrop=e=>{
    e.preventDefault();const s=dragShapeRef.current;if(!s)return;
    const r=svgRef.current.getBoundingClientRect();
    const x=(e.clientX-r.left-pan.x)/zoom-s.w/2,y=(e.clientY-r.top-pan.y)/zoom-s.h/2;
    const id=uid(),nn={id,type:s.type,x,y,w:s.w||DEF_W,h:s.h||DEF_H,label:s.label,color:"#8b949e"};
    const np=pages.map(p=>p.id===activeId?{...p,nodes:[...p.nodes,nn]}:p);
    setPages(np);setSelNode(id);setSelEdge(null);pushH(np);dragShapeRef.current=null;
  };

  const duplicateNode=useCallback(()=>{
    if(!selNode)return;const src=nodes.find(n=>n.id===selNode);if(!src)return;
    const id=uid(),n2={...src,id,x:src.x+20,y:src.y+20};
    const np=pages.map(p=>p.id===activeId?{...p,nodes:[...p.nodes,n2]}:p);
    setPages(np);setSelNode(id);pushH(np);swal.success("Duplicated!");
  },[selNode,nodes,pages,activeId,pushH]);

  const deleteSelected=useCallback((sn,se)=>{
    if(sn){const np=pages.map(p=>p.id===activeId?{...p,nodes:p.nodes.filter(n=>n.id!==sn),edges:p.edges.filter(e=>e.from!==sn&&e.to!==sn)}:p);setPages(np);setSelNode(null);pushH(np);swal.success("Deleted.");}
    else if(se){const np=pages.map(p=>p.id===activeId?{...p,edges:p.edges.filter(e=>e.id!==se)}:p);setPages(np);setSelEdge(null);pushH(np);swal.success("Edge deleted.");}
  },[nodes,edges,pages,activeId,pushH]);

  const snRef=useRef(selNode),seRef=useRef(selEdge);
  useEffect(()=>{snRef.current=selNode;},[selNode]);
  useEffect(()=>{seRef.current=selEdge;},[selEdge]);
  useEffect(()=>{
    const fn=e=>{
      if(["INPUT","TEXTAREA","SELECT"].includes(document.activeElement.tagName))return;
      if(e.key==="Delete"||e.key==="Backspace")deleteSelected(snRef.current,seRef.current);
      if((e.metaKey||e.ctrlKey)&&e.key==="z"&&!e.shiftKey){e.preventDefault();undo();}
      if((e.metaKey||e.ctrlKey)&&(e.key==="y"||(e.shiftKey&&e.key==="z"))){e.preventDefault();redo();}
      if((e.metaKey||e.ctrlKey)&&e.key==="d"){e.preventDefault();duplicateNode();}
      if((e.metaKey||e.ctrlKey)&&e.key==="s"){e.preventDefault();setShowSave(true);}
      if(e.key==="Escape"){setConn(null);setConnMode(false);}
    };
    window.addEventListener("keydown",fn);return()=>window.removeEventListener("keydown",fn);
  },[deleteSelected,undo,redo,duplicateNode]);

  const setNP=(prop,val)=>{if(!selNode)return;const np=pages.map(p=>p.id===activeId?{...p,nodes:p.nodes.map(n=>n.id===selNode?{...n,[prop]:val}:n)}:p);setPages(np);pushH(np);};
  const setEP=(prop,val)=>{if(!selEdge)return;const np=pages.map(p=>p.id===activeId?{...p,edges:p.edges.map(e=>e.id===selEdge?{...e,[prop]:val}:e)}:p);setPages(np);pushH(np);};

  const getSVGPt=e=>{const r=svgRef.current.getBoundingClientRect();return{x:(e.clientX-r.left-pan.x)/zoom,y:(e.clientY-r.top-pan.y)/zoom};};

  const onNodePointerDown=useCallback((e,node)=>{
    e.stopPropagation();
    if(e.shiftKey||connectMode){setConn(node);setSelNode(null);setSelEdge(null);svgRef.current.setPointerCapture(e.pointerId);return;}
    setSelNode(node.id);setSelEdge(null);
    const pt=getSVGPt(e);setDrag({nodeId:node.id,ox:pt.x-node.x,oy:pt.y-node.y});svgRef.current.setPointerCapture(e.pointerId);
  },[pan,zoom,connectMode]);

  const onResizeStart=useCallback((e,node,handle)=>{
    e.stopPropagation();const pt=getSVGPt(e);
    setResize({nodeId:node.id,handle,ox:pt.x,oy:pt.y,origX:node.x,origY:node.y,origW:node.w,origH:node.h});
    svgRef.current.setPointerCapture(e.pointerId);setDrag(null);
  },[pan,zoom]);

  const onSVGDown=e=>{
    if(e.target===svgRef.current||e.target.tagName==="rect"||e.target.tagName==="svg"){
      setSelNode(null);setSelEdge(null);
      if(e.altKey||e.button===1){setPanDrag({sx:e.clientX-pan.x,sy:e.clientY-pan.y});svgRef.current.setPointerCapture(e.pointerId);}
    }
  };

  const onSVGMove=e=>{
    const r=svgRef.current.getBoundingClientRect();
    const pt={x:(e.clientX-r.left-pan.x)/zoom,y:(e.clientY-r.top-pan.y)/zoom};
    setMouse(pt);
    if(panDrag){setPan({x:e.clientX-panDrag.sx,y:e.clientY-panDrag.sy});return;}
    if(drag){setNodes(ns=>ns.map(n=>n.id===drag.nodeId?{...n,x:pt.x-drag.ox,y:pt.y-drag.oy}:n));return;}
    if(resize){
      const{nodeId,handle,ox,oy,origX,origY,origW,origH}=resize;
      const dx=pt.x-ox,dy=pt.y-oy;
      let nx=origX,ny=origY,nw=origW,nh=origH;
      if(handle.includes("e"))nw=Math.max(MIN_W,origW+dx);
      if(handle.includes("s"))nh=Math.max(MIN_H,origH+dy);
      if(handle.includes("w")){nx=origX+dx;nw=Math.max(MIN_W,origW-dx);}
      if(handle.includes("n")){ny=origY+dy;nh=Math.max(MIN_H,origH-dy);}
      setNodes(ns=>ns.map(n=>n.id===nodeId?{...n,x:nx,y:ny,w:nw,h:nh}:n));
    }
  };

  const onSVGUp=e=>{
    if(connecting){
      const r=svgRef.current.getBoundingClientRect();
      const pt={x:(e.clientX-r.left-pan.x)/zoom,y:(e.clientY-r.top-pan.y)/zoom};
      const tgt=nodes.find(n=>pt.x>=n.x&&pt.x<=n.x+n.w&&pt.y>=n.y&&pt.y<=n.y+n.h&&n.id!==connecting.id);
      if(tgt&&!edges.some(ed=>ed.from===connecting.id&&ed.to===tgt.id)){
        const ne=[...edges,{id:uid(),from:connecting.id,to:tgt.id,label:"",connStyle:"curved",lineStyle:"solid",color:""}];
        const np=pages.map(p=>p.id===activeId?{...p,edges:ne}:p);setPages(np);pushH(np);swal.success("Connected!");
      }else if(!tgt)swal.warning("Drop on another node!");
      setConn(null);
    }
    if(drag)pushH(pages);
    if(resize)pushH(pages);
    setDrag(null);setResize(null);setPanDrag(null);
  };

  useEffect(()=>{
    const el=svgRef.current;
    const fn=e=>{e.preventDefault();setZoom(z=>Math.min(Math.max(z*(e.deltaY>0?.9:1.1),.1),6));};
    el.addEventListener("wheel",fn,{passive:false});return()=>el.removeEventListener("wheel",fn);
  },[]);

  const openEdit=()=>{if(!selNode)return;const n=nodes.find(n=>n.id===selNode);if(!n)return;setEditVal(n.label.replace(/\\n/g,"\n"));setEditModal(selNode);};
  const saveEdit=()=>{const v=editVal.replace(/\n/g,"\\n");const np=pages.map(p=>p.id===activeId?{...p,nodes:p.nodes.map(n=>n.id===editModal?{...n,label:v}:n)}:p);setPages(np);pushH(np);setEditModal(null);swal.success("Saved!");};
  const onNodeDblClick=id=>{setSelNode(id);setSelEdge(null);const n=nodes.find(n=>n.id===id);if(!n)return;setEditVal(n.label.replace(/\\n/g,"\n"));setEditModal(id);};
  const openEditEdge=async(eid,cur)=>{const r=await swal.prompt("Edge Label","Label...",cur||"");if(!r.isConfirmed)return;setEP("label",r.value?.trim()||"");swal.success("Updated!");};

  const fitScreen=()=>{
    if(nodes.length===0)return;
    const el=svgRef.current,W=el.clientWidth,H=el.clientHeight;
    const xs=nodes.map(n=>n.x),ys=nodes.map(n=>n.y);
    const minX=Math.min(...xs)-20,minY=Math.min(...ys)-20,maxX=Math.max(...nodes.map(n=>n.x+n.w))+20,maxY=Math.max(...nodes.map(n=>n.y+n.h))+20;
    const cz=Math.min(Math.max(Math.min(W/(maxX-minX),H/(maxY-minY)),.1),4);
    setZoom(cz);setPan({x:W/2-(minX+maxX)/2*cz,y:H/2-(minY+maxY)/2*cz});swal.info("Fitted!");
  };
  const exportPNG=()=>{
    const el=svgRef.current,W=el.clientWidth,H=el.clientHeight;const cl=el.cloneNode(true);cl.setAttribute("xmlns","http://www.w3.org/2000/svg");cl.setAttribute("width",W);cl.setAttribute("height",H);
    const s64=btoa(unescape(encodeURIComponent(new XMLSerializer().serializeToString(cl))));
    const cv=document.createElement("canvas");cv.width=W*2;cv.height=H*2;const ctx=cv.getContext("2d");ctx.scale(2,2);ctx.fillStyle=T.canvas;ctx.fillRect(0,0,W,H);
    const img=new Image();img.onload=()=>{ctx.drawImage(img,0,0,W,H);const a=document.createElement("a");a.download=`${currentTitle}.png`;a.href=cv.toDataURL("image/png");a.click();swal.success("PNG exported!");};img.src="data:image/svg+xml;base64,"+s64;
  };
  const exportSVG=()=>{const el=svgRef.current;const cl=el.cloneNode(true);cl.setAttribute("xmlns","http://www.w3.org/2000/svg");const blob=new Blob([new XMLSerializer().serializeToString(cl)],{type:"image/svg+xml"});const a=document.createElement("a");a.download=`${currentTitle}.svg`;a.href=URL.createObjectURL(blob);a.click();swal.success("SVG exported!");};
  const handleLoad=data=>{
    const nn=(data.nodes||[]).map(n=>({id:n.nodeKey,type:n.type,label:n.label,color:n.color,x:n.x,y:n.y,w:n.w||DEF_W,h:n.h||DEF_H}));
    const ne=(data.edges||[]).map(e=>({id:e.edgeKey,from:e.fromNodeKey,to:e.toNodeKey,label:e.label||"",connStyle:"curved",lineStyle:"solid"}));
    const np=[{id:uid(),name:"Loaded",nodes:nn,edges:ne}];setPages(np);setActiveId(np[0].id);setCurrentId(data.id);setCurrentTitle(data.title);setSelNode(null);setSelEdge(null);pushH(np);
  };

  const sno=nodes.find(n=>n.id===selNode);
  const seo=edges.find(e=>e.id===selEdge);
  const IS=mI(T);
  const PL={fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:3,marginTop:7};
  const DIV={height:1,background:T.border,margin:"8px 0"};
  const loggedInUser=getLoggedInUser();

  return (
<div ref={rootRef} style={{display:"flex",flexDirection:"column",height:"calc(98vh - 60px)",background:T.bg,color:T.text,fontFamily:"'Segoe UI',system-ui,sans-serif",overflow:"hidden",fontSize:12}}>
      {/* MENU BAR */}
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"0 10px",height:36,background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:5,marginRight:5}}>
          <div style={{width:20,height:20,borderRadius:4,background:T.accent,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:10,color:"#fff"}}>FC</div>
          <span style={{fontWeight:700,fontSize:13}}>FlowCraft</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:99,background:T.panel,border:`1px solid ${T.border}`}}>
          <div style={{width:15,height:15,borderRadius:"50%",background:T.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:"#fff"}}>{loggedInUser.fullName.charAt(0).toUpperCase()}</div>
          <span style={{fontSize:10,color:T.muted}}>{loggedInUser.fullName}</span>
        </div>
        {currentId&&<span style={{fontSize:10,color:T.muted}}>#{currentId} ·</span>}
        <span style={{fontSize:11,fontWeight:600,maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{currentTitle}</span>
        <div style={{flex:1}}/>
        {localSaved&&<span style={{fontSize:10,color:T.success,display:"flex",alignItems:"center",gap:3}}><span style={{width:5,height:5,borderRadius:"50%",background:T.success,display:"inline-block"}}/>Saved</span>}
        <button onClick={()=>setShowSave(true)} style={{...mB(T,T.accent),fontSize:11,padding:"3px 11px"}}>💾 {currentId?"Update":"Save"}</button>
        <button onClick={()=>setShowLoad(true)} style={{...mB(T,T.accent,true),fontSize:11,padding:"3px 11px"}}>📂 Load</button>
        <button onClick={async()=>{const r=await swal.confirm("Clear?","All nodes/edges removed.");if(r.isConfirmed){const pid=uid(),np=[{id:pid,name:"Page-1",nodes:[],edges:[]}];setPages(np);setActiveId(pid);setSelNode(null);setSelEdge(null);setCurrentId(null);setCurrentTitle("Untitled Flowchart");pushH(np);swal.success("Cleared!");}}} style={{...mB(T),fontSize:11,padding:"3px 9px"}}>↺ New</button>
      </div>

      {/* TOOLBAR */}
      <div style={{display:"flex",alignItems:"center",gap:1,padding:"2px 5px",background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0,flexWrap:"wrap",minHeight:30}}>
        <TB onClick={undo} title="Undo (Ctrl+Z)" disabled={histIdx<=0} T={T}>↩ Undo</TB>
        <TB onClick={redo} title="Redo (Ctrl+Y)" disabled={histIdx>=history.length-1} T={T}>↪ Redo</TB>
        <div style={{width:1,height:14,background:T.border,margin:"0 2px"}}/>
        <TB onClick={()=>deleteSelected(selNode,selEdge)} title="Delete (Del)" disabled={!selNode&&!selEdge} danger T={T}>🗑</TB>
        <TB onClick={duplicateNode} title="Duplicate (Ctrl+D)" disabled={!selNode} T={T}>⧉</TB>
        <div style={{width:1,height:14,background:T.border,margin:"0 2px"}}/>
        <TB onClick={()=>setZoom(z=>Math.min(z*1.2,6))} T={T}>+</TB>
        <span style={{fontSize:10,color:T.muted,minWidth:34,textAlign:"center"}}>{Math.round(zoom*100)}%</span>
        <TB onClick={()=>setZoom(1)} T={T}>1:1</TB>
        <TB onClick={()=>setZoom(z=>Math.max(z*0.8,.1))} T={T}>−</TB>
        <TB onClick={fitScreen} T={T}>⊡ Fit</TB>
        <div style={{width:1,height:14,background:T.border,margin:"0 2px"}}/>
        <TB onClick={exportPNG} T={T}>⬇ PNG</TB>
        <TB onClick={exportSVG} T={T}>⬇ SVG</TB>
        <div style={{width:1,height:14,background:T.border,margin:"0 2px"}}/>
        <TB onClick={()=>setConnMode(c=>!c)} active={connectMode} title="Connect (Shift+drag)" T={T}>🔗 {connectMode?"Connecting":"Connect"}</TB>
        <div style={{width:1,height:14,background:T.border,margin:"0 2px"}}/>
        <TB onClick={toggleTheme} T={T}>{isDark?"☀️":"🌙"}</TB>
        <TB onClick={toggleFS} active={isFS} T={T}>{isFS?"✕ Exit":"⤢ Full"}</TB>
        <div style={{flex:1}}/>
        <span style={{fontSize:10,color:T.muted,paddingRight:4}}>{nodes.length}n · {edges.length}e</span>
      </div>

      {/* MAIN */}
      <div style={{display:"flex",flex:1,overflow:"hidden"}}>
        {/* LEFT */}
        <div style={{width:182,background:T.surface,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column",flexShrink:0,overflow:"hidden"}}>
          <ShapeLibrary onAdd={addNode} onDragStart={onShapeDragStart} T={T}/>
        </div>

        {/* CANVAS */}
        <div style={{flex:1,position:"relative",overflow:"hidden"}} onDragOver={onCanvasDragOver} onDrop={onCanvasDrop}>
          <svg ref={svgRef} width="100%" height="100%"
            onPointerDown={onSVGDown} onPointerMove={onSVGMove} onPointerUp={onSVGUp}
            style={{display:"block",cursor:connecting?"crosshair":connectMode?"cell":panDrag?"grabbing":resize?CURSORS[resize.handle]:"default"}}>
            <defs>
              <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform={`translate(${pan.x%20},${pan.y%20})`}>
                <circle cx="1" cy="1" r=".7" fill={T.gridColor} opacity=".7"/>
              </pattern>
              <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse" patternTransform={`translate(${pan.x%100},${pan.y%100})`}>
                <path d="M 100 0 L 0 0 0 100" fill="none" stroke={T.gridColor} strokeWidth=".4" opacity=".4"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={T.canvas}/>
            <rect width="100%" height="100%" fill="url(#dots)"/>
            <rect width="100%" height="100%" fill="url(#grid)"/>
            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              {edges.map(e=><Arrow key={e.id} edge={e} nodes={nodes} selected={e.id===selEdge} T={T} onSelect={id=>{setSelEdge(id);setSelNode(null);}} onDblClick={openEditEdge}/>)}
              {connecting&&<line x1={connecting.x+connecting.w/2} y1={connecting.y+connecting.h/2} x2={mouse.x} y2={mouse.y} stroke={T.accent} strokeWidth={2} strokeDasharray="6 3" style={{pointerEvents:"none"}}/>}
              {nodes.map(n=><FlowNode key={n.id} node={n} selected={n.id===selNode} T={T} onPointerDown={onNodePointerDown} onDblClick={onNodeDblClick} onResizeStart={onResizeStart}/>)}
            </g>
          </svg>

          {nodes.length===0&&(<div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",pointerEvents:"none",gap:8}}>
            <div style={{fontSize:56,opacity:0.04}}>◈</div>
            <div style={{fontSize:14,color:T.muted,fontWeight:600,opacity:0.4}}>Canvas is empty</div>
            <div style={{fontSize:12,color:T.border,textAlign:"center",lineHeight:1.9,opacity:0.6}}>Click or drag a shape from the left panel</div>
          </div>)}

          {connectMode&&!connecting&&<div style={{position:"absolute",top:10,left:"50%",transform:"translateX(-50%)",background:T.warning+"22",border:`1px solid ${T.warning}`,borderRadius:20,padding:"3px 14px",fontSize:11,color:T.warning,pointerEvents:"none"}}>🔗 Shift+drag or click node to start</div>}
          {connectMode&&connecting&&<div style={{position:"absolute",top:10,left:"50%",transform:"translateX(-50%)",background:T.accent+"22",border:`1px solid ${T.accent}`,borderRadius:20,padding:"3px 14px",fontSize:11,color:T.accent,pointerEvents:"none"}}>→ Click target node</div>}

          {/* STATUS */}
          <div style={{position:"absolute",bottom:0,left:0,right:0,display:"flex",alignItems:"center",gap:7,padding:"2px 9px",background:T.surface+"dd",borderTop:`1px solid ${T.border}`,fontSize:10,color:T.muted}}>
            <span>{Math.round(zoom*100)}%</span><span>·</span>
            <span>{nodes.length}n {edges.length}e</span><span>·</span>
            <span>{activePage?.name}</span>
            {sno&&<><span>·</span><span>x:{Math.round(sno.x)} y:{Math.round(sno.y)} w:{Math.round(sno.w)} h:{Math.round(sno.h)}</span></>}
          </div>
        </div>

        {/* RIGHT: Properties */}
        <div style={{width:218,background:T.surface,borderLeft:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0,padding:"9px 11px 20px"}}>
          <div style={{fontSize:10,fontWeight:700,color:T.accent,letterSpacing:2,textTransform:"uppercase",marginBottom:9}}>Properties</div>

          {sno ? (
            <>
              <div style={PL}>Type</div>
              <span style={{display:"inline-block",padding:"2px 8px",borderRadius:99,background:T.panel,border:`1px solid ${T.border}`,fontSize:10,marginBottom:4}}>{sno.type}</span>
              <div style={PL}>Label</div>
              <div onClick={openEdit} style={{fontSize:11,color:T.text,marginBottom:5,wordBreak:"break-all",whiteSpace:"pre-wrap",cursor:"pointer",padding:"4px 6px",borderRadius:4,border:`1px dashed ${T.border}`,minHeight:20}}>
                {sno.label.replace(/\\n/g,"\n")||<span style={{color:T.muted,fontStyle:"italic"}}>click to edit</span>}
              </div>
              <button style={{...mB(T,T.accent,true),width:"100%",marginBottom:5,fontSize:11}} onClick={openEdit}>✏ Edit Label</button>
              <div style={DIV}/>
              <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:5}}>Size & Position</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,marginBottom:3}}>
                {[["W","w"],["H","h"],["X","x"],["Y","y"]].map(([lbl,prop])=>(
                  <div key={prop}>
                    <div style={{fontSize:9,color:T.muted,marginBottom:2}}>{lbl}</div>
                    <input type="number" style={{...IS,padding:"3px 5px",fontSize:11}} value={Math.round(sno[prop])} onChange={e=>setNP(prop,Number(e.target.value))}/>
                  </div>
                ))}
              </div>
              <div style={{fontSize:9,color:T.muted,marginBottom:5,opacity:0.6}}>↔ Drag corner handles to resize</div>
              <div style={DIV}/>
              <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:5}}>Style</div>
              <div style={PL}>Border Color</div>
              <ColorPicker current={sno.color||"#8b949e"} onChange={v=>setNP("color",v)} T={T}/>
              <div style={PL}>Fill Color</div>
              <div style={{display:"flex",gap:3,marginBottom:3}}>
                <button onClick={()=>setNP("fillColor","transparent")} style={{...mB(T,!sno.fillColor||sno.fillColor==="transparent"?T.accent:undefined,!(!sno.fillColor||sno.fillColor==="transparent")),fontSize:10,padding:"2px 7px"}}>None</button>
                <input type="color" value={sno.fillColor&&sno.fillColor!=="transparent"?sno.fillColor:"#1c2333"} onChange={e=>setNP("fillColor",e.target.value)} style={{flex:1,height:22,border:`1px solid ${T.border}`,borderRadius:3,cursor:"pointer",background:T.panel,padding:"1px"}}/>
              </div>
              <div style={PL}>Text Color</div>
              <ColorPicker current={sno.textColor||sno.color||"#8b949e"} onChange={v=>setNP("textColor",v)} T={T}/>
              <div style={{display:"flex",gap:3,marginTop:5,marginBottom:3}}>
                <div style={{flex:1}}>
                  <div style={PL}>Size</div>
                  <select style={{...IS,padding:"2px 4px",fontSize:11}} value={sno.fontSize||12} onChange={e=>setNP("fontSize",Number(e.target.value))}>
                    {FONT_SIZES.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div style={PL}>Style</div>
                  <div style={{display:"flex",gap:2}}>
                    <button onClick={()=>setNP("bold",!sno.bold)} style={{...mB(T,sno.bold?T.accent:undefined,!sno.bold),padding:"2px 6px",fontWeight:700}}>B</button>
                    <button onClick={()=>setNP("italic",!sno.italic)} style={{...mB(T,sno.italic?T.accent:undefined,!sno.italic),padding:"2px 6px",fontStyle:"italic"}}>I</button>
                  </div>
                </div>
              </div>
              <div style={DIV}/>
              <div style={{display:"flex",gap:4}}>
                <button style={{...mB(T,T.accent,true),flex:1,fontSize:11}} onClick={duplicateNode}>⧉ Dupe</button>
                <button style={{...mB(T,T.danger),flex:1,fontSize:11}} onClick={()=>deleteSelected(selNode,null)}>🗑 Delete</button>
              </div>
            </>
          ) : seo ? (
            <>
              <div style={PL}>Connection</div>
              <div style={{fontSize:11,color:T.text,lineHeight:1.8,marginBottom:7}}>
                <span style={{fontWeight:600}}>{nodes.find(n=>n.id===seo.from)?.label.split("\\n")[0]||"?"}</span><br/>
                <span style={{color:T.muted,fontSize:10}}>↓</span><br/>
                <span style={{fontWeight:600}}>{nodes.find(n=>n.id===seo.to)?.label.split("\\n")[0]||"?"}</span>
              </div>
              <div style={PL}>Label</div>
              <input style={{...IS,marginBottom:5}} value={seo.label||""} onChange={e=>setEP("label",e.target.value)} placeholder="Edge label..."/>
              <div style={PL}>Style</div>
              <select style={{...IS,marginBottom:4}} value={seo.connStyle||"curved"} onChange={e=>setEP("connStyle",e.target.value)}>
                {CONN_STYLES.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
              <div style={PL}>Line</div>
              <select style={{...IS,marginBottom:5}} value={seo.lineStyle||"solid"} onChange={e=>setEP("lineStyle",e.target.value)}>
                {LINE_STYLES.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
              <div style={PL}>Color</div>
              <ColorPicker current={seo.color||T.muted} onChange={v=>setEP("color",v)} T={T}/>
              <div style={DIV}/>
              <button style={{...mB(T,T.danger),width:"100%",fontSize:11}} onClick={()=>deleteSelected(null,selEdge)}>🗑 Delete Edge</button>
            </>
          ) : (
            <>
              <div style={{fontSize:11,color:T.muted,lineHeight:1.8,marginBottom:8}}>Select a node or edge.</div>
              <div style={{padding:9,borderRadius:5,background:T.panel,border:`1px solid ${T.border}`,marginBottom:7}}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:4}}>Shortcuts</div>
                {[["Click shape","Add"],["Drag shape","Drop"],["Shift+drag","Connect"],["Dbl-click","Edit label"],["Alt+drag","Pan"],["Scroll","Zoom"],["Del","Delete"],["Ctrl+Z/Y","Undo/Redo"],["Ctrl+D","Duplicate"],["Ctrl+S","Save"],["Esc","Cancel"]].map(([k,v])=>(
                  <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:10,padding:"1px 0",color:T.muted}}>
                    <span style={{color:T.text,fontFamily:"monospace",fontSize:9}}>{k}</span><span>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{padding:9,borderRadius:5,background:T.panel,border:`1px solid ${T.border}`}}>
                <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:4}}>Canvas</div>
                <div style={{fontSize:10,color:T.muted,lineHeight:1.9}}>
                  <div>Pages: <b style={{color:T.text}}>{pages.length}</b></div>
                  <div>Active: <b style={{color:T.text}}>{activePage?.name}</b></div>
                  <div>Nodes: <b style={{color:T.text}}>{nodes.length}</b></div>
                  <div>Edges: <b style={{color:T.text}}>{edges.length}</b></div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* PAGE TABS */}
      <PageTabs pages={pages} activeId={activeId} onSwitch={id=>{setActiveId(id);setSelNode(null);setSelEdge(null);}} onAdd={addPage} onRename={renamePage} onDelete={deletePage} T={T}/>

      {/* MODALS */}
      {showSave&&<SaveModal pages={pages} folders={folders} currentId={currentId} currentTitle={currentTitle} onClose={()=>setShowSave(false)} onSaved={(id,t)=>{setCurrentId(id);setCurrentTitle(t);}} T={T}/>}
      {showLoad&&<LoadModal onClose={()=>setShowLoad(false)} onLoad={handleLoad} T={T}/>}
      {editModal&&(
        <div style={{position:"fixed",inset:0,background:"#00000099",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500}} onClick={()=>setEditModal(null)}>
          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,padding:18,minWidth:310,boxShadow:T.shadow}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:12,fontWeight:700,color:T.accent,marginBottom:10}}>✏ Edit Label</div>
            <textarea style={{width:"100%",minHeight:75,background:T.panel,border:`1px solid ${T.border}`,borderRadius:4,color:T.text,padding:"6px 9px",fontSize:12,resize:"vertical",boxSizing:"border-box",outline:"none"}}
              value={editVal} onChange={e=>setEditVal(e.target.value)} autoFocus
              onKeyDown={e=>{if(e.key==="Enter"&&e.ctrlKey)saveEdit();if(e.key==="Escape")setEditModal(null);}}
              placeholder="Label text (Ctrl+Enter to save)"/>
            <div style={{fontSize:9,color:T.muted,margin:"4px 0 11px"}}>Ctrl+Enter save · Esc cancel</div>
            <div style={{display:"flex",gap:6}}>
              <button style={{...mB(T,T.accent),flex:1}} onClick={saveEdit}>✔ Save</button>
              <button style={{...mB(T),flex:1}} onClick={()=>setEditModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}